import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import UiContent from '../../../Components/Common/UiContent';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getSupplyByCenterQuery } from '../../../slices/fuelSupply/thunk';
import SupplyByCenterOffcanvas from './Modals/SupplyByCenterOffcanvas';
import SupplyByCenterSection from './SupplyByCenterSection';

dayjs.extend(utc);

const SupplyByCenter = () => {
	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio' },
		{ to: '#', label: 'Medio Ambiente' },
		{ to: '#', label: 'Emisiones' },
		{ to: '#', label: 'Reportes' },
	];

	const [isRight, setIsRight] = useState(false);
	const [filters, setFilters] = useState({ date_range: [] });

	const toggleRightCanvas = useCallback(() => {
		setIsRight(prev => !prev);
	}, []);

	const handleFiltersSubmit = useCallback(newFilters => {
		setFilters(newFilters);
	}, []);

	// Construir baseFilters con register_date en formato ISO para el backend
	const baseFilters = useMemo(() => {
		const dateRange = filters.date_range || [];
		if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
			return {
				supply_date: {
					$between: [
						dayjs.utc(dateRange[0]).startOf('day').toISOString(),
						dayjs.utc(dateRange[1]).endOf('day').toISOString(),
					],
				},
			};
		}
		return undefined;
	}, [filters.date_range]);

	function formatNumber(value) {
		if (value == null || value === 0) return '-';
		const fixedValue = Number(value).toFixed(1);
		const [integerPart, decimalPart] = fixedValue.split('.');
		const formattedInteger = integerPart.replace(
			/\B(?=(\d{3})+(?!\d))/g,
			'.',
		);
		return `${formattedInteger},${decimalPart}`;
	}

	const columns = useMemo(
		() => [
			{
				header: 'Establecimiento',
				accessorKey: 'establishment_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Año',
				accessorKey: 'year',
				accessorFn: row =>
					typeof row.year === 'string'
						? row.year
						: (row.year?.toString() ?? ''),
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Mes',
				accessorKey: 'month',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Tipo Combustible',
				accessorKey: 'fuel_type_name',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Total Abastecimiento',
				accessorKey: 'total_supply',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.total_supply;
					if (value == null || value <= 0) return '-';
					return (
						<span className='badge border border-success text-success'>
							{formatNumber(value)}
						</span>
					);
				},
			},
			{
				header: 'Unidad',
				accessorKey: 'unit_measurement_name',
				width: '1%',
				dataType: 'multi-select',
			},
		],
		[],
	);

	return (
		<React.Fragment>
			<UiContent />
			<div className='page-content'>
				<Container fluid>
					<BreadCrumb
						title='Abastecimiento de Combustibles por Establecimiento'
						links={breadcrumbLinks}
					/>

					<Row>
						<Col lg={12}>
							<Card>
								<CardHeader className='border-0 pb-0'>
									<SupplyByCenterSection
										filters={filters}
										rightCanvasBtn={toggleRightCanvas}
									/>

									<SupplyByCenterOffcanvas
										isOpen={isRight}
										toggle={toggleRightCanvas}
										filters={filters}
										onSubmit={handleFiltersSubmit}
									/>
								</CardHeader>

								<CardBody>
									<ODataGrid
										key={`grid_supply_establishment_${JSON.stringify(filters.date_range)}`}
										columns={columns}
										nameFile='abastecimiento_por_establecimiento'
										exportDateField='supply_date'
										baseFilters={baseFilters}
										getDataFunc={getSupplyByCenterQuery}
										remoteOperations={{
											pagination: true,
											sorting: true,
											filtering: true,
										}}
									/>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default SupplyByCenter;
